import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { Role, User } from '@src/app/model/userdata';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, firstValueFrom, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { Scope } from '@ess/jg-rule-executor';
import { RetailerScope } from '@src/app/model/retailer-scope';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  standalone: false,
})
export class EditUserComponent implements OnInit, OnDestroy {
  public allRoleNames: string[] = [];
  private _rolesWithoutUserAndAdmin: string[] = [];
  public rolesControl: FormControl = new FormControl();

  public allScopes: RetailerScope[] = [];
  public scopesControl: FormControl = new FormControl();
  public showScopeSelect: boolean = false;

  private _subs = new Subscription();

  constructor(
    private _dialogRef: MatDialogRef<EditUserComponent>,
    private _apolloService: ApolloService,
    private _translateService: TranslateService,
    private _scopeService: ScopeService,
    @Inject(MAT_DIALOG_DATA) public data: { user: User }
  ) {
  }

  closeDialog(): void {
    this._dialogRef.close();
  }

  async ngOnInit(): Promise<void> {
    this.allRoleNames = await firstValueFrom(this._apolloService.getAllRoles$());
    this._rolesWithoutUserAndAdmin = this.allRoleNames.filter(role => role != 'user' && role != 'admin');
    this.showScopeSelect = this._canShowScopeSelect(this.data.user.roles);

    this.rolesControl.setValue(this._findMostSignificantRoleName(this.data.user.roles));
    this._subs.add(this.rolesControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe(async (selectedRole) => {
        const success = firstValueFrom(this._apolloService.setRoles$(this.data.user.id, [selectedRole]));
        if (!success) {
          alert(`${this._translateService.instant('editUser.roles.failure')} ${this.data.user.email}`);
        } else {
          this.data.user.roles = [new Role(selectedRole, [], [], false)];
          this.showScopeSelect = this._canShowScopeSelect(this.data.user.roles);
        }
      })
    );

    this.allScopes = await firstValueFrom(this._scopeService.scopes$);
    // map ids to names
    this.scopesControl.setValue(
      this.data.user.scopes.map(
        r => this.allScopes.find(s => s.id.toString() === r.scope)?.name
      )
    );
    this._subs.add(this.scopesControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe(async (selectedScopes) => {
        // map names back to ids
        const scopeIds = (selectedScopes as string[]).map(
          s => this.allScopes.find(scope => scope.name === s)?.id.toString()
        );
        const success = firstValueFrom(this._apolloService.setScopes$(this.data.user.id, scopeIds));
        if (!success) {
          alert(`${this._translateService.instant('editUser.scopes.failure')} ${this.data.user.email}`);
        } else {
          this.data.user.scopes = scopeIds.map(r => new Scope(r));
        }
      })
    );
  }

  public ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private async performSimpleOp(
    op: (user: User) => Observable<boolean>,
    failureKey: string,
    successKey: string
  ): Promise<void> {
    const success = await firstValueFrom(op(this.data.user)).catch(
      () => alert(`${this._translateService.instant(failureKey)} ${this.data.user.email}`)
    );
    if (!success) {
      alert(`${this._translateService.instant(failureKey)} ${this.data.user.email}`);
    } else {
      alert(`${this._translateService.instant(successKey)} ${this.data.user.email}`);
    }
  }

  async resetMfa(): Promise<void> {
    return this.performSimpleOp(
      u => this._apolloService.resetMfa$(u.id),
      'editUser.mfa.failure',
      'editUser.mfa.success'
    );
  }

  async toggleBlock(): Promise<void> {
    return this.performSimpleOp(
      u => this._apolloService.blockUser$(u.id, !this.data.user.blocked).pipe(
        tap((b) => {
          if (b) {
            this.data.user.blocked = !this.data.user.blocked;
          }
        })
      ),
      this.data.user.blocked ? 'editUser.unblock.failure' : 'editUser.block.failure',
      this.data.user.blocked ? 'editUser.unblock.success' : 'editUser.block.success'
    );
  }

  /**
   * Format the role(s) to put in the select input
   * Because all roles are children of the root 'user' role, and because in the "old style" a user could have multiple roles,
   * we need to convert the roles to a single role for the select input. Note that selecting a non-user role will always
   * result in the user role being added as well (because, as mentioned, all roles are children of the user role).
   *
   * @param roles
   * @private
   */
  private _findMostSignificantRoleName(roles: Role[]): string {
    if (roles.length === 0) {
      return 'user';
    }
    if (roles.length === 1) {
      return roles[0].name;
    }
    const roleNames = roles.map(r => r.name);
    if (roleNames.includes('admin')) {
      return 'admin';
    } else if (roleNames.includes('retailer_manager')) {
      return 'retailer_manager';
    } else if (roleNames.includes('module_manager')) {
      return 'module_manager';
    } else if (roleNames.includes('retailer')) {
      return 'retailer';
    } else {
      return 'user';
    }
  }

  private _canShowScopeSelect(roles: Role[]): boolean {
    return roles.some(r => this._rolesWithoutUserAndAdmin.includes(r.name));
  }
}
